import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  AccountBalance as AccountBalanceIcon,
  Payments as PaymentsIcon,
  TrendingUp as TrendingUpIcon,
  RemoveShoppingCart as RemoveShoppingCartIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DashboardYearlyChart from '../components/dashboard/DashboardYearlyChart';
import MonthlyMetricColumn from '../components/dashboard/MonthlyMetricColumn';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import { hasPermission } from '../utils/permissions';
import axios from 'axios';
import { formatCurrency as formatMoney } from '../utils/currency';
import { usePageNotice } from '../context/FeedbackContext';

const EMPTY_MANAGER_METRICS = {
  certified: { stoneCount: 0, totalCarat: 0, totalBuyValue: 0 },
  lots: { totalAvailableCarat: 0, totalBuyValue: 0 },
  revenue: { totalOpenBalance: 0 },
  metricsCurrency: null,
  calculatedAt: null,
};

const EMPTY_ACTIVITY_METRICS = {
  today: {
    payments: { count: 0, total: 0 },
    orders: { count: 0, total: 0 },
    canceledNoReturn: { count: 0, total: 0 },
  },
  month: {
    payments: { count: 0, total: 0 },
    orders: { count: 0, total: 0 },
    canceledNoReturn: { count: 0, total: 0 },
  },
};

const EMPTY_MONTHLY_METRICS = {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  finalized: false,
  metricsCurrency: null,
  payments: { count: 0, total: 0, byMethod: [] },
  orders: { count: 0, total: 0, totalCarats: 0 },
  canceled: {
    count: 0,
    totalCarats: 0,
    returnedToInventory: { count: 0, totalCarats: 0 },
    notReturned: { count: 0, total: 0, totalCarats: 0 },
  },
};

const EMPTY_YEARLY_METRICS = {
  year: null,
  metricsCurrency: null,
  months: [],
};

const MONTH_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function buildYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= current - 10; y -= 1) {
    years.push(y);
  }
  return years;
}

const formatCountMoney = (count, money, formatMoney) =>
  `${count.toLocaleString()} · ${formatMoney(money)}`;

const formatCarat = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ct`;

const Dashboard = () => {
  const { activeCompany, isClient, userRole, customPermissions } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isManager = userRole === ROLES.MANAGER;

  const canViewInventoryMetrics = hasPermission(userRole, 'view_dashboard_inventory', customPermissions);
  const canViewOpenBalance = hasPermission(userRole, 'view_dashboard_open_balance', customPermissions);
  const canViewToday = hasPermission(userRole, 'view_dashboard_today', customPermissions);
  const canViewMonthlyPayments = hasPermission(userRole, 'view_dashboard_monthly_payments', customPermissions);
  const canViewMonthlyOrders = hasPermission(userRole, 'view_dashboard_monthly_orders', customPermissions);
  const canViewMonthlyCancellations = hasPermission(userRole, 'view_dashboard_monthly_cancellations', customPermissions);
  const canSelectPrefMonths = hasPermission(userRole, 'allow_pref_months_metrics', customPermissions);
  const canViewManagerSnapshot = canViewInventoryMetrics || canViewOpenBalance;
  const canViewMonthly = canViewMonthlyPayments || canViewMonthlyOrders || canViewMonthlyCancellations;

  const [managerMetrics, setManagerMetrics] = useState(EMPTY_MANAGER_METRICS);
  const [managerMetricsLoading, setManagerMetricsLoading] = useState(false);
  const [managerMetricsRefreshing, setManagerMetricsRefreshing] = useState(false);
  const [managerMetricsError, setManagerMetricsError] = useState(null);
  const [activityMetrics, setActivityMetrics] = useState(EMPTY_ACTIVITY_METRICS);
  const [activityMetricsLoading, setActivityMetricsLoading] = useState(false);
  const [activityMetricsError, setActivityMetricsError] = useState(null);
  const now = new Date();
  const [monthlyYear, setMonthlyYear] = useState(now.getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(now.getMonth() + 1);
  const [monthlyMetrics, setMonthlyMetrics] = useState(EMPTY_MONTHLY_METRICS);
  const [monthlyMetricsLoading, setMonthlyMetricsLoading] = useState(false);
  const [monthlyMetricsError, setMonthlyMetricsError] = useState(null);
  const [yearlyMetrics, setYearlyMetrics] = useState(EMPTY_YEARLY_METRICS);
  const [yearlyMetricsLoading, setYearlyMetricsLoading] = useState(false);
  const [yearlyMetricsError, setYearlyMetricsError] = useState(null);
  const [mobileGraphsOpen, setMobileGraphsOpen] = useState({
    payments: false,
    orders: false,
    cancellations: false,
  });
  const yearOptions = buildYearOptions();

  const toggleMobileGraphs = (key) => {
    setMobileGraphsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchManagerMetrics = useCallback(async () => {
    setManagerMetricsLoading(true);
    setManagerMetricsError(null);
    try {
      const res = await axios.get('/api/dashboard/manager-metrics');
      setManagerMetrics(res.data || EMPTY_MANAGER_METRICS);
    } catch (error) {
      console.error('Failed to fetch manager metrics:', error);
      setManagerMetricsError(
        error.userMessage
        || error.response?.data?.error
        || t('dashboard.metricsLoadFailed'),
      );
    } finally {
      setManagerMetricsLoading(false);
    }
  }, [t]);

  const refreshManagerMetrics = useCallback(async () => {
    setManagerMetricsRefreshing(true);
    setManagerMetricsError(null);
    try {
      const res = await axios.post('/api/dashboard/manager-metrics/refresh');
      setManagerMetrics(res.data || EMPTY_MANAGER_METRICS);
    } catch (error) {
      console.error('Failed to refresh manager metrics:', error);
      const isRateLimited = error.response?.status === 429;
      setManagerMetricsError(
        error.userMessage
        || (isRateLimited ? t('common.rateLimited') : null)
        || error.response?.data?.error
        || t('dashboard.metricsRefreshFailed'),
      );
    } finally {
      setManagerMetricsRefreshing(false);
    }
  }, [t]);

  const fetchActivityMetrics = useCallback(async () => {
    setActivityMetricsLoading(true);
    setActivityMetricsError(null);
    try {
      const res = await axios.get('/api/dashboard/activity-metrics');
      setActivityMetrics(res.data || EMPTY_ACTIVITY_METRICS);
    } catch (error) {
      setActivityMetricsError(
        error.userMessage
        || error.response?.data?.error
        || t('dashboard.activity.loadFailed'),
      );
    } finally {
      setActivityMetricsLoading(false);
    }
  }, [t]);

  const fetchMonthlyMetrics = useCallback(async (year, month) => {
    setMonthlyMetricsLoading(true);
    setMonthlyMetricsError(null);
    try {
      const res = await axios.get('/api/dashboard/metrics/monthly', {
        params: { year, month },
      });
      setMonthlyMetrics(res.data || EMPTY_MONTHLY_METRICS);
    } catch (error) {
      setMonthlyMetricsError(
        error.userMessage
        || error.response?.data?.error
        || t('dashboard.monthly.loadFailed'),
      );
    } finally {
      setMonthlyMetricsLoading(false);
    }
  }, [t]);

  const fetchYearlyMetrics = useCallback(async (year) => {
    setYearlyMetricsLoading(true);
    setYearlyMetricsError(null);
    try {
      const res = await axios.get('/api/dashboard/metrics/yearly', {
        params: { year },
      });
      setYearlyMetrics(res.data || EMPTY_YEARLY_METRICS);
    } catch (error) {
      setYearlyMetricsError(
        error.userMessage
        || error.response?.data?.error
        || t('dashboard.monthly.yearlyLoadFailed'),
      );
    } finally {
      setYearlyMetricsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canViewManagerSnapshot) fetchManagerMetrics();
    else {
      setManagerMetricsError(null);
      setManagerMetricsLoading(false);
    }
  }, [activeCompany, canViewManagerSnapshot, fetchManagerMetrics]);

  useEffect(() => {
    if (canViewToday) fetchActivityMetrics();
    else {
      setActivityMetricsError(null);
      setActivityMetricsLoading(false);
    }
  }, [activeCompany, canViewToday, fetchActivityMetrics]);

  useEffect(() => {
    if (!canSelectPrefMonths) {
      const current = new Date();
      setMonthlyYear(current.getFullYear());
      setMonthlyMonth(current.getMonth() + 1);
    }
  }, [canSelectPrefMonths]);

  useEffect(() => {
    if (canViewMonthly) fetchMonthlyMetrics(monthlyYear, monthlyMonth);
    else {
      setMonthlyMetricsError(null);
      setMonthlyMetricsLoading(false);
    }
  }, [activeCompany, canViewMonthly, monthlyYear, monthlyMonth, fetchMonthlyMetrics]);

  useEffect(() => {
    if (canViewMonthly) fetchYearlyMetrics(monthlyYear);
    else {
      setYearlyMetricsError(null);
      setYearlyMetricsLoading(false);
      setYearlyMetrics(EMPTY_YEARLY_METRICS);
    }
  }, [activeCompany, canViewMonthly, monthlyYear, fetchYearlyMetrics]);

  usePageNotice(
    'dashboard-manager-metrics',
    canViewManagerSnapshot && managerMetricsError
      ? { message: managerMetricsError, severity: 'error', persist: true }
      : null,
  );
  usePageNotice(
    'dashboard-activity-metrics',
    canViewToday && activityMetricsError
      ? { message: activityMetricsError, severity: 'error', persist: true }
      : null,
  );
  usePageNotice(
    'dashboard-monthly-metrics',
    canViewMonthly && monthlyMetricsError
      ? { message: monthlyMetricsError, severity: 'error', persist: true }
      : null,
  );
  usePageNotice(
    'dashboard-yearly-metrics',
    canViewMonthly && yearlyMetricsError
      ? { message: yearlyMetricsError, severity: 'error', persist: true }
      : null,
  );

  if (isClient) return <Navigate to="/inventory" replace />;

  const formatCalculatedAt = (iso) => {
    if (!iso) return t('dashboard.metricsNeverRefreshed');
    return t('dashboard.metricsLastUpdated', {
      date: new Date(iso).toLocaleString(),
    });
  };

  const StatCard = ({ title, value, icon, color, onClick, caption, details, fillHeight = true, 'data-testid': dataTestId }) => {
    const inner = (
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="body2"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              {value}
            </Typography>
            {caption && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {caption}
              </Typography>
            )}
            {Array.isArray(details) && details.map((line, index) => (
              <Typography
                key={`${index}-${line}`}
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {line}
              </Typography>
            ))}
            {onClick && (
              <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
                {t('dashboard.metricsDetail.viewBreakdown')}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, fontSize: { xs: 30, sm: 40 } }}>{icon}</Box>
        </Box>
      </CardContent>
    );

    return (
      <Card
        sx={{
          height: fillHeight ? '100%' : 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        data-testid={dataTestId}
      >
        {onClick ? (
          <CardActionArea
            onClick={onClick}
            sx={{
              height: fillHeight ? '100%' : 'auto',
              alignItems: 'stretch',
            }}
          >
            {inner}
          </CardActionArea>
        ) : (
          inner
        )}
      </Card>
    );
  };

  const MetricSection = ({ title, children }) => (
    <Box sx={{ mt: { xs: 3, sm: 4 } }}>
      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {title}
      </Typography>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {children}
      </Grid>
    </Box>
  );

  const metricsCurrency = monthlyMetrics.metricsCurrency
    || yearlyMetrics.metricsCurrency
    || managerMetrics.metricsCurrency
    || 'USD';
  const formatMetricMoney = (n) => formatMoney(n, metricsCurrency);

  const yearlyMonths = Array.isArray(yearlyMetrics.months) ? yearlyMetrics.months : [];
  const paymentsYearPoints = yearlyMonths.map((m) => ({
    month: m.month,
    value: m.payments?.total || 0,
  }));
  const ordersYearPoints = yearlyMonths.map((m) => ({
    month: m.month,
    value: m.orders?.total || 0,
    carats: m.orders?.totalCarats || 0,
  }));
  const cancellationsYearPoints = yearlyMonths.map((m) => ({
    month: m.month,
    value: m.canceled?.notReturned?.total || 0,
    carats: m.canceled?.totalCarats || 0,
  }));

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          {t('dashboard.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {t('dashboard.welcome', { name: activeCompany?.companyName || 'Pich Jewels' })}
        </Typography>

        {isManager && canViewManagerSnapshot && (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={managerMetricsRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={refreshManagerMetrics}
              disabled={managerMetricsLoading || managerMetricsRefreshing}
            >
              {t('dashboard.refreshMetrics')}
            </Button>
            {!managerMetricsLoading && (
              <Typography variant="body2" color="text.secondary">
                {formatCalculatedAt(managerMetrics.calculatedAt)}
              </Typography>
            )}
          </Box>
        )}

        {canViewManagerSnapshot && managerMetricsLoading && (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {canViewManagerSnapshot && !managerMetricsLoading && (
          <>
            {canViewInventoryMetrics && (
              <>
                <MetricSection title={t('dashboard.sections.certified')}>
                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title={t('dashboard.certified.stoneCount')}
                      value={managerMetrics.certified.stoneCount}
                      icon={<InventoryIcon />}
                      color="primary.main"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title={t('dashboard.certified.totalCarat')}
                      value={formatCarat(managerMetrics.certified.totalCarat)}
                      icon={<InventoryIcon />}
                      color="success.main"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title={t('dashboard.certified.totalBuyValue')}
                      value={formatMetricMoney(managerMetrics.certified.totalBuyValue)}
                      icon={<InventoryIcon />}
                      color="secondary.main"
                    />
                  </Grid>
                </MetricSection>

                <MetricSection title={t('dashboard.sections.lots')}>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t('dashboard.lots.totalCarat')}
                      value={formatCarat(managerMetrics.lots.totalAvailableCarat)}
                      icon={<CategoryIcon />}
                      color="success.main"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t('dashboard.lots.totalBuyValue')}
                      value={formatMetricMoney(managerMetrics.lots.totalBuyValue)}
                      icon={<CategoryIcon />}
                      color="secondary.main"
                    />
                  </Grid>
                </MetricSection>
              </>
            )}

            {canViewOpenBalance && (
              <MetricSection title={t('dashboard.sections.revenue')}>
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard
                    title={t('dashboard.revenue.openBalance')}
                    value={formatMetricMoney(managerMetrics.revenue.totalOpenBalance)}
                    icon={<AccountBalanceIcon />}
                    color={(managerMetrics.revenue.totalOpenBalance || 0) > 0 ? 'error.main' : 'success.main'}
                    onClick={() => navigate('/dashboard/metrics/open-balance')}
                    data-testid="dashboard-open-balance-metric"
                  />
                </Grid>
              </MetricSection>
            )}
          </>
        )}

        {canViewToday && activityMetricsLoading && (
          <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {canViewToday && !activityMetricsLoading && (
          <MetricSection title={t('dashboard.activity.sectionToday')}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title={t('dashboard.activity.payments')}
                value={formatCountMoney(
                  activityMetrics.today.payments.count,
                  activityMetrics.today.payments.total,
                  formatMetricMoney,
                )}
                icon={<PaymentsIcon />}
                color="success.main"
                onClick={() => navigate('/dashboard/metrics/payments?period=today')}
                data-testid="dashboard-today-payments-metric"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title={t('dashboard.activity.orders')}
                value={formatCountMoney(
                  activityMetrics.today.orders.count,
                  activityMetrics.today.orders.total,
                  formatMetricMoney,
                )}
                icon={<TrendingUpIcon />}
                color="primary.main"
                onClick={() => navigate('/dashboard/metrics/orders?period=today')}
                data-testid="dashboard-today-orders-metric"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title={t('dashboard.activity.canceledNoReturn')}
                value={formatCountMoney(
                  activityMetrics.today.canceledNoReturn.count,
                  activityMetrics.today.canceledNoReturn.total,
                  formatMetricMoney,
                )}
                icon={<RemoveShoppingCartIcon />}
                color="warning.main"
                onClick={() => navigate('/dashboard/metrics/canceled-no-return?period=today')}
                data-testid="dashboard-today-canceled-metric"
              />
            </Grid>
          </MetricSection>
        )}

        {canViewMonthly && (
            <Box sx={{ mt: { xs: 3, sm: 4 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {t('dashboard.monthly.title')}
              </Typography>
              {canSelectPrefMonths && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label={t('dashboard.monthly.selectYear')}
                    value={monthlyYear}
                    onChange={(e) => setMonthlyYear(Number(e.target.value))}
                    sx={{ minWidth: 120 }}
                    data-testid="dashboard-monthly-year"
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label={t('dashboard.monthly.selectMonth')}
                    value={monthlyMonth}
                    onChange={(e) => setMonthlyMonth(Number(e.target.value))}
                    sx={{ minWidth: 120 }}
                    data-testid="dashboard-monthly-month"
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                  {!monthlyMetricsLoading && (
                    <Typography variant="body2" color="text.secondary">
                      {monthlyMetrics.finalized
                        ? t('dashboard.monthly.finalizedHint')
                        : t('dashboard.monthly.liveHint')}
                    </Typography>
                  )}
                </Box>
              )}

              {monthlyMetricsLoading ? (
                <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                (() => {
                  const monthlyColumns = [
                    canViewMonthlyPayments && {
                      key: 'payments',
                      sectionTitle: t('dashboard.activity.payments'),
                      graphsExpanded: mobileGraphsOpen.payments,
                      onToggleGraphs: () => toggleMobileGraphs('payments'),
                      card: (
                        <StatCard
                          title={t('dashboard.monthly.selectedMonthSummary')}
                          value={formatCountMoney(
                            monthlyMetrics.payments?.count || 0,
                            monthlyMetrics.payments?.total || 0,
                            formatMetricMoney,
                          )}
                          icon={<PaymentsIcon />}
                          color="success.main"
                          fillHeight={!isMobile}
                          details={(monthlyMetrics.payments?.byMethod || []).map((entry) => (
                            t('dashboard.monthly.paymentMethodLine', {
                              method: t(`paymentMethods.${entry.method}`, { defaultValue: entry.method }),
                              count: entry.count,
                              value: formatMetricMoney(entry.total),
                            })
                          ))}
                          onClick={() => navigate(
                            `/dashboard/metrics/payments?year=${monthlyYear}&month=${monthlyMonth}`,
                          )}
                          data-testid="dashboard-monthly-payments"
                        />
                      ),
                      chart: (
                        <DashboardYearlyChart
                          title={t('dashboard.monthly.yearlyChartTitle', { year: monthlyYear })}
                          points={paymentsYearPoints}
                          selectedMonth={monthlyMonth}
                          formatValue={formatMetricMoney}
                          valueLegend={t('dashboard.monthly.yearlyValueLegend')}
                          emptyLabel={t('dashboard.monthly.yearlyEmpty')}
                          data-testid="dashboard-yearly-payments"
                        />
                      ),
                    },
                    canViewMonthlyOrders && {
                      key: 'orders',
                      sectionTitle: t('dashboard.monthly.orders'),
                      graphsExpanded: mobileGraphsOpen.orders,
                      onToggleGraphs: () => toggleMobileGraphs('orders'),
                      card: (
                        <StatCard
                          title={t('dashboard.monthly.selectedMonthSummary')}
                          value={formatCountMoney(
                            monthlyMetrics.orders?.count || 0,
                            monthlyMetrics.orders?.total || 0,
                            formatMetricMoney,
                          )}
                          icon={<TrendingUpIcon />}
                          color="primary.main"
                          fillHeight={!isMobile}
                          caption={t('dashboard.monthly.caratsOrdered', {
                            carats: Number(monthlyMetrics.orders?.totalCarats || 0)
                              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                          })}
                          onClick={() => navigate(
                            `/dashboard/metrics/orders?year=${monthlyYear}&month=${monthlyMonth}`,
                          )}
                          data-testid="dashboard-monthly-orders"
                        />
                      ),
                      chart: (
                        <DashboardYearlyChart
                          title={t('dashboard.monthly.yearlyChartTitle', { year: monthlyYear })}
                          points={ordersYearPoints}
                          selectedMonth={monthlyMonth}
                          formatValue={formatMetricMoney}
                          valueLegend={t('dashboard.monthly.yearlyValueLegend')}
                          caratsLegend={t('dashboard.monthly.yearlyCaratsLegend')}
                          emptyLabel={t('dashboard.monthly.yearlyEmpty')}
                          data-testid="dashboard-yearly-orders"
                        />
                      ),
                    },
                    canViewMonthlyCancellations && {
                      key: 'cancellations',
                      sectionTitle: t('dashboard.monthly.cancellations'),
                      graphsExpanded: mobileGraphsOpen.cancellations,
                      onToggleGraphs: () => toggleMobileGraphs('cancellations'),
                      card: (
                        <StatCard
                          title={t('dashboard.monthly.selectedMonthSummary')}
                          value={formatCarat(monthlyMetrics.canceled?.totalCarats || 0)}
                          icon={<RemoveShoppingCartIcon />}
                          color="warning.main"
                          fillHeight={!isMobile}
                          details={[
                            t('dashboard.monthly.notReturnedCarats', {
                              carats: Number(monthlyMetrics.canceled?.notReturned?.totalCarats || 0)
                                .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                            }),
                            t('dashboard.monthly.notReturnedValue', {
                              value: formatMetricMoney(monthlyMetrics.canceled?.notReturned?.total || 0),
                            }),
                          ]}
                          onClick={() => navigate(
                            `/dashboard/metrics/canceled-no-return?year=${monthlyYear}&month=${monthlyMonth}`,
                          )}
                          data-testid="dashboard-monthly-cancellations"
                        />
                      ),
                      chart: (
                        <DashboardYearlyChart
                          title={t('dashboard.monthly.yearlyChartTitle', { year: monthlyYear })}
                          points={cancellationsYearPoints}
                          selectedMonth={monthlyMonth}
                          formatValue={formatMetricMoney}
                          valueLegend={t('dashboard.monthly.yearlyNotReturnedValueLegend')}
                          caratsLegend={t('dashboard.monthly.yearlyCaratsLegend')}
                          emptyLabel={t('dashboard.monthly.yearlyEmpty')}
                          data-testid="dashboard-yearly-cancellations"
                        />
                      ),
                    },
                  ].filter(Boolean);

                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: `repeat(${monthlyColumns.length || 1}, minmax(0, 1fr))`,
                        },
                        columnGap: { xs: 2, sm: 3 },
                        rowGap: { xs: 2, sm: 2 },
                        alignItems: 'stretch',
                      }}
                    >
                      {monthlyColumns.map((col, index) => (
                        <MonthlyMetricColumn
                          key={col.key}
                          sectionTitle={col.sectionTitle}
                          chartKey={col.key}
                          columnIndex={index}
                          columnCount={monthlyColumns.length}
                          isMobile={isMobile}
                          chartsLoading={yearlyMetricsLoading}
                          graphsExpanded={col.graphsExpanded}
                          onToggleGraphs={col.onToggleGraphs}
                          card={col.card}
                          chart={col.chart}
                        />
                      ))}
                    </Box>
                  );
                })()
              )}
              {yearlyMetricsLoading && (
                <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
            </Box>
        )}
      </Container>
    </Layout>
  );
};

export default Dashboard;
